/**
 * BUTTON TOKENS
 * Source: 🎨 Figma — components-rentals export
 *
 * Custom additions are marked with "Custom" comments below.
 */

import { type SemanticTokens, SEMANTIC } from '../semantic.js';

export function createButtonTokens(s: SemanticTokens) {
  return {
  // ──── Figma tokens ────

  /** Base spacing for all button variants */
  base: {
    paddingX: s.layout.gap.md,  // --button-base-padding-x: var(--spacing-16)
    paddingY: s.layout.gap.md,  // --button-base-padding-y: var(--spacing-16)
    gap: s.layout.gap.sm,       // --button-base-gap: var(--spacing-8)
    radius: s.radius.md,        // --button-base-radius: var(--radius-8)
    transitionDuration: s.transition.normal,  // --button-base-transition-duration
    labelFontWeight: s.typography.fontWeight.bold, // --button-base-label-font-weight
    labelLineHeight: s.typography.lineHeight.xl,   // --button-base-label-line-height (1.5rem)
    focusRingColor: s.focus.ring,   // --button-base-focus-ring-color: #c14f79 (primary)
    focusRingWidth: '2px',          // --button-base-focus-ring-width
    focusRingStyle: 'solid',        // --button-base-focus-ring-style
    focusRingOffset: '2px',         // --button-base-focus-ring-offset
  },
  /** Small button sizing */
  sm: {
    fontSize: s.typography.fontSize.base,     // --button-sm-font-size
    padding: s.layout.padding.xs,             // --button-sm-padding
  },
  /** Large button sizing */
  lg: {
    fontSize: s.typography.fontSize.xl,       // --button-lg-font-size
    padding: s.layout.padding.md,             // --button-lg-padding
  },
  /** Primary button (main CTA) */
  primary: {
    bg: s.interactive.primary.base,          // --button-primary-bg: var(--color-rentals-800)
    bgHover: s.interactive.primary.hover,    // --button-primary-bg-hover: var(--color-rentals-900)
    bgPressed: s.interactive.primary.pressed, // --button-primary-bg-pressed: var(--color-rentals-950)
    bgDisabled: s.surface.disabled,          // --button-primary-bg-disabled: var(--surface-disabled)
    fg: s.text.onColor,                      // --button-primary-fg: var(--text-on-color)
    fgDisabled: s.text.onColor,              // --button-primary-fg-disabled: var(--text-on-color)
    border: s.border.primary,                // --button-primary-border: var(--border-primary)
  },
  /** Secondary button (neutral actions) */
  secondary: {
    bg: s.interactive.secondary.base,        // --button-secondary-bg: var(--color-rentsody-800)
    bgHover: s.interactive.secondary.hover,  // --button-secondary-bg-hover: var(--color-rentsody-700)
    bgPressed: s.interactive.secondary.pressed, // --button-secondary-bg-pressed: var(--color-rentsody-950)
    bgDisabled: s.surface.disabled,          // --button-secondary-bg-disabled: var(--surface-disabled)
    fg: s.text.onColor,                      // --button-secondary-fg: var(--text-on-color)
    fgDisabled: s.text.onColor,              // --button-secondary-fg-disabled: var(--text-on-color)
    border: s.border.secondary,              // --button-secondary-border: var(--border-secondary)
  },
  /** Ghost button (minimal/outline) */
  ghost: {
    bg: s.surface.ground,                    // --button-info-bg: var(--surface-ground)
    bgHover: s.interactive.secondary.subtle, // --button-info-bg-hover: var(--color-rentsody-50)
    bgPressed: s.interactive.secondary.subtlePressed, // --button-info-bg-pressed: var(--color-rentsody-200)
    bgDisabled: s.surface.disabled,          // --button-info-bg-disabled: var(--surface-disabled)
    fg: s.text.secondary,                    // --button-info-fg: var(--text-secondary)
    fgDisabled: s.text.onColor,              // --button-info-fg-disabled: var(--text-on-color)
    border: s.border.secondary,              // --button-info-border: var(--border-secondary)
    /** Ghost button specific spacing */
    paddingX: s.layout.gap.md,               // --button-info-button-padding-x: var(--spacing-16)
    paddingY: s.layout.gap.md,               // --button-info-button-padding-y: var(--spacing-16)
    gap: s.layout.gap.sm,                    // --button-info-button-gap: var(--spacing-8)
    fontWeight: s.typography.fontWeight.bold, // --button-ghost-font-weight
    fontSize: s.typography.fontSize.base,     // --button-ghost-font-size
  },
  /** Action button (icon + label, contextual actions) */
  action: {
    bg: s.surface.ground,                    // --button-action-bg: var(--surface-ground)
    bgHover: s.interactive.primary.subtle,   // --button-action-bg-hover: var(--color-rentals-50)
    bgPressed: s.interactive.primary.subtlePressed, // --button-action-bg-pressed: var(--color-rentals-100)
    bgDisabled: s.surface.disabled,          // --button-action-bg-disabled: var(--surface-disabled)
    fg: s.text.secondary,                   // --button-action-fg: var(--color-rentsody-800)
    fgHover: s.text.primary,                 // --button-action-fg-hover: var(--text-primary)
    fgDisabled: s.text.onColor,              // --button-action-fg-disabled: var(--text-on-color)
    border: s.border.secondary,              // --button-action-border: var(--border-secondary)
    size: s.controlSize.md,                  // --button-action-size (2.75rem)
    iconSize: s.iconSizes.lg,                // --button-action-icon-size
    fontWeight: s.typography.fontWeight.bold, // --button-action-font-weight
    fontSize: s.typography.fontSize.base,     // --button-action-font-size
    transition: s.transition.fast,           // --button-action-transition
  },
  /** Utility button (icon-only, compact) */
  utility: {
    bg: s.interactive.secondary.subtle,      // --button-utility-bg: var(--color-rentsody-50)
    bgHover: s.interactive.secondary.subtleHover, // --button-utility-bg-hover: var(--color-rentsody-100)
    bgPressed: s.interactive.secondary.subtlePressed, // --button-utility-bg-pressed: var(--color-rentsody-200)
    bgDisabled: s.surface.disabled,          // --button-utility-bg-disabled: var(--surface-disabled)
    fg: s.text.secondary,                    // --button-utility-fg: var(--text-secondary)
    fgDisabled: s.text.onColor,              // --button-utility-fg-disabled: var(--text-on-color)
    border: s.border.secondary,              // --button-utility-border: var(--border-secondary)
    size: s.controlSize.md,                  // --button-utility-size (2.75rem)
    iconSize: s.iconSizes.lg,                // --button-utility-icon-size
    transition: s.transition.fast,           // --button-utility-transition
  },
  /** Status button variants */
  status: {
    success: {
      bg: s.interactive.success.base,        // --button-status-success-bg: var(--color-green-800)
      bgHover: s.interactive.success.hover,  // --button-status-success-bg-hover: var(--color-green-900)
      bgPressed: s.interactive.success.pressed, // --button-status-success-bg-pressed: var(--color-green-950)
      bgDisabled: s.surface.disabled,        // --button-status-success-bg-disabled: var(--surface-disabled)
      fg: s.text.onColor,                    // --button-status-success-fg: var(--text-on-color)
      fgDisabled: s.text.onColor,            // --button-status-success-fg-disabled: var(--text-on-color)
      border: s.border.success,              // --button-status-success-border: var(--border-success)
    },
    danger: {
      bg: s.interactive.danger.base,         // --button-status-danger-bg: var(--color-red-800)
      bgHover: s.interactive.danger.hover,   // --button-status-danger-bg-hover: var(--color-red-900)
      bgPressed: s.interactive.danger.pressed, // --button-status-danger-bg-pressed: var(--color-red-950)
      bgDisabled: s.surface.disabled,        // --button-status-danger-bg-disabled: var(--surface-disabled)
      fg: s.text.onColor,                    // --button-status-danger-fg: var(--text-on-color)
      fgDisabled: s.text.onColor,            // --button-status-danger-fg-disabled: var(--text-on-color)
      border: s.border.danger,               // --button-status-danger-border: var(--border-danger)
    },
    warning: {
      bg: s.interactive.warning.base,        // --button-status-warning-bg: var(--color-yellow-800)
      bgHover: s.interactive.warning.hover,  // --button-status-warning-bg-hover: var(--color-yellow-900)
      bgPressed: s.interactive.warning.pressed, // --button-status-warning-bg-pressed: var(--color-yellow-950)
      bgDisabled: s.surface.disabled,        // --button-status-warning-bg-disabled: var(--surface-disabled)
      fg: s.text.onColor,                    // --button-status-warning-fg: var(--text-on-color)
      fgDisabled: s.text.onColor,            // --button-status-warning-fg-disabled: var(--text-on-color)
      border: s.border.warning,              // --button-status-warning-border: var(--border-warning)
    },
  },

  // ──── Custom tokens (not in Figma) ────

  /** Outlined button variant */
  outlined: {
    fg: s.text.primary,                      // --button-outlined-fg: var(--text-primary)
    border: s.border.default,                // --button-outlined-border: var(--border-default)
  },
  /** Text button variant */
  text: {
    fg: s.text.primary,                      // --button-text-fg: var(--text-primary)
  },
  /** Link button variant */
  link: {
    fg: s.text.link,                         // --button-link-fg: var(--text-link)
    fgHover: s.text.linkHover,               // --button-link-fg-hover: var(--text-link-hover)
    secondaryFg: s.text.secondary,           // --button-link-secondary-fg: var(--text-secondary)
    fontSize: s.typography.fontSize.sm,       // --button-link-font-size
    lineHeight: s.typography.lineHeight.lg,   // --button-link-line-height
    fontWeight: s.typography.fontWeight.bold, // --button-link-font-weight
    fontWeightSem: s.typography.fontWeight.semibold, // --button-link-semibold-font-weight
    gap: s.layout.gap.xs,                    // --button-link-gap
    iconSize: s.iconSizes.base,              // --button-link-icon-size
    transition: s.transition.fast,           // --button-link-transition
  },
  /** Sidebar button styles */
  sidebar: {
    border: s.border.primary,                // --button-sidebar-border: var(--border-primary)
    gap: s.layout.gap.sm,                    // --button-sidebar-gap
    padding: s.layout.gap.md,                // --button-sidebar-padding
    borderWidth: s.layout.gap.xs,            // --button-sidebar-border-width
    iconSize: s.iconSizes.lg,                // --button-sidebar-icon-size
    fontWeightNormal: s.typography.fontWeight.normal, // --button-sidebar-font-weight-normal
    fontWeightBold: s.typography.fontWeight.bold,     // --button-sidebar-font-weight-bold
    transition: s.transition.fast,           // --button-sidebar-transition
  },
  };
}
export const buttonTokens = createButtonTokens(SEMANTIC);